import { useRef } from "react";

/**
 * Keep the `<video>` src stable across refetches.
 *
 * The API mints a NEW short-lived signed URL every time a review is fetched
 * (the signature + expiry differ per mint), so binding it straight to
 * `<video src>` means any refetch — resolving a comment, posting a reply,
 * creating a share link — swaps the src and makes the browser reload the video.
 * That resets playback to 0, which re-derives the active comment and snaps the
 * comment panel back to the top: the user loses their place mid-review.
 *
 * The URL stays valid for hours, so we hold the first one we receive for a
 * review and only take a new one when the review itself changes (e.g. switching
 * versions). Returns the live value until the first non-null URL arrives, so a
 * still-analyzing review still shows the video the moment it becomes available.
 */
export function useStableVideoUrl(
  reviewId: string | undefined,
  videoUrl: string | null | undefined,
): string | null {
  const held = useRef<{ reviewId: string; url: string } | null>(null);

  // Deterministic render-phase memo: only ever fills a slot that is empty or
  // belongs to a different review, so re-running it is a no-op.
  if (reviewId && videoUrl && held.current?.reviewId !== reviewId) {
    held.current = { reviewId, url: videoUrl };
  }

  const current = held.current;
  return current && current.reviewId === reviewId ? current.url : (videoUrl ?? null);
}
